import { useCallback, useEffect, useMemo, useState } from 'react';

export type CommunityConnection = 'checking' | 'online' | 'offline';

const SECOND = 1_000;
const MINUTE = 60 * SECOND;

function relativeCheckLabel(checkedAt: number | null, now: number): string {
  if (checkedAt == null) return 'checking now';
  const elapsed = Math.max(0, now - checkedAt);
  if (elapsed < 10 * SECOND) return 'checked just now';
  if (elapsed < MINUTE) return `checked ${Math.floor(elapsed / SECOND)}s ago`;
  return `checked ${Math.floor(elapsed / MINUTE)}m ago`;
}

function relativeActivityLabel(activityAt: number | null, now: number): string | null {
  if (activityAt == null) return null;
  const elapsed = Math.max(0, now - activityAt);
  if (elapsed < MINUTE) return 'latest take just now';
  if (elapsed < 60 * MINUTE) return `latest take ${Math.floor(elapsed / MINUTE)}m ago`;
  const hours = Math.floor(elapsed / (60 * MINUTE));
  if (hours < 24) return `latest take ${hours}h ago`;
  return `latest take ${Math.floor(hours / 24)}d ago`;
}

/**
 * The North Bank's honest connectivity signal. It records completed checks,
 * rather than implying that polling means new editorial or supporter activity.
 */
export function useCommunityAccess() {
  const [connection, setConnection] = useState<CommunityConnection>('checking');
  const [checkedAt, setCheckedAt] = useState<number | null>(null);

  useEffect(() => {
    const offline = () => setConnection('offline');
    const online = () => setConnection((current) => current === 'offline' ? 'checking' : current);
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    return () => {
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
    };
  }, []);

  const recordCheck = useCallback((online: boolean) => {
    setConnection(online ? 'online' : 'offline');
    setCheckedAt(Date.now());
  }, []);

  return { connection, checkedAt, recordCheck };
}

export function CommunityActivityStatus({
  connection,
  checkedAt,
  latestActivityAt,
  publicTakesLastHour,
  checkIntervalSeconds,
  adaptive = false,
}: {
  connection: CommunityConnection;
  checkedAt: number | null;
  latestActivityAt: number | null;
  publicTakesLastHour?: number | null;
  checkIntervalSeconds: number;
  adaptive?: boolean;
}) {
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 10 * SECOND);
    return () => window.clearInterval(timer);
  }, []);

  const checkLabel = relativeCheckLabel(checkedAt, now);
  const activityLabel = relativeActivityLabel(latestActivityAt, now);
  const label = useMemo(() => {
    if (connection === 'checking') return 'Checking the North Bank';
    if (connection === 'offline') return `Offline · saved conversation · ${checkLabel}`;
    const activityCount = publicTakesLastHour == null
      ? ''
      : publicTakesLastHour === 0
        ? ' · quiet this hour'
        : ` · ${publicTakesLastHour} public ${publicTakesLastHour === 1 ? 'take' : 'takes'} this hour`;
    return `Connected · ${checkLabel}${activityLabel ? ` · ${activityLabel}` : ''}${activityCount}`;
  }, [activityLabel, checkLabel, connection, publicTakesLastHour]);

  return (
    <div
      className={`community-activity community-activity--${connection}`}
      role="status"
      aria-live="off"
      aria-label={`${label}. ${adaptive ? `Checks start every ${checkIntervalSeconds} seconds and back off when unchanged` : `Checks every ${checkIntervalSeconds} seconds`} while this page is open.`}
      title={`${adaptive ? `Checks start every ${checkIntervalSeconds} seconds and back off when unchanged` : `Checks every ${checkIntervalSeconds} seconds`} while this page is open. This is connectivity, not a claim that new activity exists.`}
    >
      <span className="community-activity-dot" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}
